import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styles from "./ManageDepartments.module.css";


const ManageDepartments = () => {
  const navigate = useNavigate();
  const adminemail = localStorage.getItem("adminemail") || "";

  // State: an array of companies (each with departments),
  //        master departments, search term, loading, error
  const [companiesData, setCompaniesData] = useState([]);
  const [masterDepts, setMasterDepts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch all companies
  const fetchCompanies = async () => {
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/sb_showcompanyusers",
        { adminemail }
      );
      if (response.data && response.data.user_data) {
        return response.data.user_data;
      } else {
        return [];
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setError("Failed to fetch companies.");
      return [];
    }
  };

  // Fetch master department types
  const fetchMasterDepartments = async () => {
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/get_master_assessment_type",
        {}
      );
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (err) {
      console.error("Error fetching master departments:", err);
      return [];
    }
  };

  // Fetch departments for a single company by its admincode
  const fetchDepartmentsByCompany = async (companyCode) => {
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/get_departments_by_company",
        { company_code: companyCode }
      );
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (err) {
      console.error(`Error fetching departments for ${companyCode}:`, err);
      return [];
    }
  };

  // On mount, load companies & master depts, then attach each company's departments
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError("");

      const companies = await fetchCompanies();
      const mDepts = await fetchMasterDepartments();

      // For each company, fetch its departments
      const companiesWithDepts = await Promise.all(
        companies.map(async (company) => {
          const departments = await fetchDepartmentsByCompany(company.admincode);
          return { ...company, departments };
        })
      );

      setCompaniesData(companiesWithDepts);
      setMasterDepts(mDepts);
      setLoading(false);
    };

    loadData();
  }, [adminemail]);

  // Toggle department status and immediately update local state
  const handleToggleDepartment = async (deptAdminCode, newStatus, companyCode) => {
    try {
      // Send update request
      await axios.post("https://casaahaanahotels.com/sb_update_status", {
        admincode: deptAdminCode,
        deptstatus: newStatus,
      });

      // Update department status in local state (no need to re-fetch)
      setCompaniesData((prev) =>
        prev.map((company) => {
          if (company.admincode === companyCode) {
            const updatedDepts = company.departments.map((dept) => {
              if (dept.admincode === deptAdminCode) {
                return { ...dept, deptstatus: newStatus };
              }
              return dept;
            });
            return { ...company, departments: updatedDepts };
          }
          return company;
        })
      );
    } catch (err) {
      console.error("Error updating department status:", err);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [previewDept, setPreviewDept] = useState(null);

  // Preview in new tab
  // const handlePreview = (deptAdminCode) => {
  //   window.open(`/user-landingpage/${deptAdminCode}`, "_blank");
  // };

  const handlePreview = (deptAdminCode) => {
    setPreviewDept(deptAdminCode);
    setShowModal(true);
  };

  const handleDevicePreview = (device) => {
    if (previewDept) {
      const url = `/user-landingpage/${previewDept}?view=${device}`;
      window.open(url, "_blank");
      setShowModal(false);
    }
  };

  // Search filter
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCompanies = companiesData.filter((company) =>
    company.companyname?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const modalRef = useRef(null);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    }

    if (showModal) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showModal]);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Manage Departments</h1>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search companies..."
          value={searchTerm}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
      </div>

      {loading ? (
        <p className={styles.loadingText}>Loading companies...</p>
      ) : error ? (
        <p className={styles.errorText}>{error}</p>
      ) : filteredCompanies.length === 0 ? (
        <p className={styles.noResults}>No companies match the search criteria.</p>
      ) : (
        <div className={styles.companiesContainer}>
          {filteredCompanies.map((company) => (
            <div key={company.admincode} className={styles.companyCard}>
              {/* Company Header */}
              <div className={styles.companyHeader}>
                {company.imageFile && (
                  <img
                    src={`https://starbehaviors.com/uploadimage/${company.imageFile}`}
                    alt={company.companyname}
                    className={styles.companyLogo}
                  />
                )}
                <h2 className={styles.companyName}>{company.companyname}</h2>
              </div>

              {/* Departments Container */}
              <div className={styles.departmentsContainer}>
                {company.departments && company.departments.length > 0 ? (
                  company.departments.map((dept) => {
                    // Map department code to a friendly name from the master list
                    const matchingMaster = masterDepts.find(
                      (master) => master.assessment_type_id === dept.company_department
                    );
                    const departmentName = matchingMaster
                      ? matchingMaster.AssessmentType
                      : "Unknown Department";
                    const statusEnabled = dept.deptstatus === 1;

                    return (
                      <div key={dept.admincode} className={styles.departmentCard}>
                        {/* Department Name + Edit Button */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                          <p className={styles.departmentName}>{departmentName}</p>

                        </div>

                        <p className={styles.statusText}>
                          Status: {statusEnabled ? "Enabled" : "Disabled"}
                        </p>

                        <div className={styles.buttonGroup}>
                          {/* Toggle Switch */}
                          <div className={styles.toggleSwitchContainer}>
                            <input
                              type="checkbox"
                              id={`toggle-${dept.admincode}`}
                              className={styles.toggleCheckbox}
                              checked={statusEnabled}
                              onChange={(e) => {
                                const newStatus = e.target.checked ? 1 : 0;
                                handleToggleDepartment(
                                  dept.admincode,     // department's admincode
                                  newStatus,
                                  company.admincode   // company's admincode
                                );
                              }}
                            />
                            <label
                              htmlFor={`toggle-${dept.admincode}`}
                              className={styles.toggleLabel}
                            />
                          </div>

                          {/* Preview button (only if enabled) */}
                          {(
                            <button
                              className={styles.previewButton}
                              onClick={() => handlePreview(dept.admincode)}
                            >
                              Preview
                            </button>
                          )}
                          <button
                            className={styles.EditButton}
                            onClick={() => navigate(`/admin/manage-department/${dept.admincode}`)}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className={styles.noDepartments}>
                    No departments found for this company.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}


      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            ref={modalRef}
            style={{
              backgroundColor: 'white',
              padding: '30px 40px',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '300px',
            }}
          >
            <h3 style={{ color: '#202A77', fontSize: '24px', marginBottom: '20px' }}>
              Choose Preview Mode
            </h3>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <button
                onClick={() => handleDevicePreview('mobile')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f7f7f7',
                  border: '1px solid black',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Mobile
              </button>
              <button
                onClick={() => handleDevicePreview('tablet')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f7f7f7',
                  border: '1px solid black',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Tablet
              </button>
              <button
                onClick={() => handleDevicePreview('desktop')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f7f7f7',
                  border: '1px solid black',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Desktop
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageDepartments;
